import { hexToOklch, oklchToHex, perceptualDistance } from '../../utils/color-oklch';

/**
 * L'INCHIOSTRO DEI SEGNI DI DESTINAZIONE, RICAVATO DALLA CASELLA.
 *
 * I due segni che dicono «puoi andare qui» — il pallino della mossa disponibile e l'anello
 * che chiude una presa multipla — non hanno un colore costante: lo derivano dal colore della
 * casella su cui cadono. È il modulo che `board-themes.ts` dichiarava di NON avere; se quel
 * commento torna a dirlo, è rimasto indietro.
 *
 * PERCHÉ DERIVARLO. Un colore costante deve reggere il fondo PEGGIORE, e quindi è troppo forte
 * su tutti gli altri. Questa evidenziazione ha già sbattuto due volte contro quel muro: prima
 * con un verde scuro velato, invisibile ovunque; poi con un verde chiaro e saturo, leggibile
 * ovunque ma acceso come un neon proprio sul legno classico, che è il fondo di quasi tutte le
 * partite. Derivarlo toglie il compromesso: il pallino può essere scuro e discreto, e a
 * scostarsi è solo dove il fondo gli somiglia.
 *
 * IL VERDE. `HL_DOT_BASE` è lo smeraldo del glow (h 152, la tinta di `--color-success` e
 * dell'alone del pezzo selezionato) tenuto a chiarezza molto più bassa: L 0.50 contro 0.66.
 *
 * La chiarezza qui non regola solo la luminosità, regola anche la SATURAZIONE, e per un giro
 * questo era sfuggito. Il gamut sRGB, su questa tinta, tiene 0.160 di croma a L 0.66 ma solo
 * 0.110 a L 0.42: una versione scesa fin laggiù aveva lo stesso identico hue del glow e si
 * leggeva come un verde spento e diverso, perché a mancare non era la tinta ma la saturazione.
 * A L 0.50 la croma disponibile è 0.134 — abbastanza perché resti smeraldo, poco abbastanza
 * perché non gridi.
 *
 * QUAL È IL NUMERO CHE CONTA. Non questo: quello che si vede è il COMPOSITO, la tinta già stesa
 * sul fondo alla sua alpha. Sul legno classico — il fondo di quasi tutte le partite — le versioni
 * provate misurano così, ed è la scala su cui sono state giudicate:
 *
 *   #27AE60 opaco    ->  #27AE60   L 0.663  C 0.160   «troppo acceso»
 *   #005D2D al 50%   ->  #5B7348   L 0.525  C 0.070   «tonalità lontana dal glow»
 *   #01934B al 50%   ->  #5B8E57   L 0.596  C 0.098   «ancora troppo acceso»
 *   QUESTO al 50%    ->  #5B8050   L 0.559  C 0.083
 *
 * E UNA TRAPPOLA MISURATA, che è il contrario di quello che verrebbe da pensare: abbassare
 * `DOT_ALPHA` NON spegne il pallino, lo SCHIARISCE. Il legno (L 0.661) è più chiaro del verde,
 * quindi meno copertura vuol dire più legno che traspare: a 0.35 di alpha il composito sale a
 * L 0.610. L'alpha smorza la saturazione, non la luminosità. Per abbassare il tono si scende
 * di `HL_DOT_BASE`, non di alpha.
 *
 * SI GIUDICA IL COLORE COMPOSITO, NON LA TINTA. È il punto in cui la versione velata di anni fa
 * aveva sbagliato: aveva scelto il colore guardandolo pieno e poi l'aveva steso al 25%, e sulla
 * casella scura la distanza reale era 0.013. Qui la scala misura ciò che si VEDE — la tinta già
 * stesa sul fondo alla sua alpha — e lo fa per entrambi i segni, il pallino a `DOT_ALPHA` e
 * l'anello al fondo del suo pulsare.
 *
 * LA SCALA PREFERISCE LO SCURO. Quando il verde di casa non si stacca abbastanza, si cerca
 * prima una chiarezza più BASSA e solo se non basta una più alta, così l'eccezione resta nello
 * spirito della regola invece di riaccendere il pallino. Su 44 fondi spediti la scala interviene
 * 11 volte e tutte e 11 verso il basso, con chiarezze fra 0.32 e 0.50.
 *
 * IL PAVIMENTO. Le soglie qui sotto stanno sopra la soglia di leggibilità del progetto (0.06,
 * `board-highlight-contrast.test.ts`), non al posto suo: sui fondi spediti il nucleo del pallino
 * misura al peggio 0.0801. Sotto quel pavimento non si scende — è la riga oltre la quale
 * un'evidenziazione si legge come una sfumatura del legno invece che come un'informazione, ed è
 * esattamente il punto in cui l'aiuto alle mosse è già stato invisibile una volta.
 */

/** Lo smeraldo del glow (h 152) tenuto a una chiarezza molto più bassa. */
export const HL_DOT_BASE = '#00773C';

/**
 * L'opacità con cui il pallino è dipinto, e con cui va quindi GIUDICATO.
 *
 * Deve restare uguale all'`opacity` di `.move-hint` in GameBoard.css: se le due divergono, la
 * scala sceglie il colore per un pallino diverso da quello che si vede. Lo verifica un test.
 */
export const DOT_ALPHA = 0.5;

/**
 * Fin dove il pallino è PIENO, in frazione del proprio raggio: oltre, sfuma fino a sparire sul
 * bordo esterno invece di finire con un taglio netto.
 *
 * È una scelta grafica — un disco che si dissolve sta sulla casella invece di essere appiccicato
 * sopra — ma ha un vincolo dietro: la garanzia di leggibilità è misurata sul NUCLEO, dove la
 * copertura è `DOT_ALPHA` piena. Ridurre questa frazione verso lo zero trasformerebbe il pallino
 * in un alone senza centro, cioè in qualcosa di più tenue di quello che il resolver ha misurato,
 * e la garanzia smetterebbe di descriverlo. Il raggio è cresciuto apposta perché il nucleo
 * restasse largo quanto il vecchio disco pieno.
 *
 * Quella scelta è poi stata rifatta al contrario. Allargare il raggio da 0.15 a 0.24 di casella
 * per far stare la sfumatura FUORI teneva sì il nucleo intatto, ma faceva crescere l'ingombro di
 * più della metà, e a schermo il pallino risultava semplicemente troppo grande. Ora il raggio è
 * tornato allo 0.15 che ha avuto per tutta la sua storia e la dissolvenza vive DENTRO: il nucleo
 * cala a 0.0975 di casella, cioè la garanzia descrive un disco più piccolo di prima ma comunque
 * un disco. È il compromesso scelto fra ingombro e nucleo; azzerare questa frazione, invece,
 * lascerebbe un alone senza centro, che la garanzia non descriverebbe più.
 */
export const DOT_CORE_FRACTION = 0.65;

/**
 * Quanto il PALLINO COMPOSITO deve staccarsi dalla casella, in distanza percettiva Oklab.
 *
 * Un terzo sopra la soglia di leggibilità del progetto (0.06): il margine che serve perché il
 * pallino resti un'informazione, non di più. Alzarla non renderebbe il pallino più bello, lo
 * renderebbe più chiaro — la scala comincerebbe a scostarsi su molti più fondi, e a scostarsi
 * verso l'alto. Abbassarla lo porta sotto il pavimento del progetto, dove è già stato invisibile.
 */
export const DOT_MIN_DISTANCE = 0.08;

/**
 * Lo stesso per l'ANELLO, giudicato al fondo del suo pulsare.
 *
 * Stesso numero e non uno più basso: siccome l'anello ha l'alpha più ALTA del pallino, il suo
 * composito sta sempre più lontano dal fondo, quindi a comandare è comunque il pallino. Tenere
 * due costanti separate serve a non doverlo riscoprire il giorno in cui una delle due alpha cambia.
 */
export const RING_MIN_DISTANCE = 0.08;

/**
 * L'alpha su cui si giudica l'anello: il FONDO del suo pulsare, non il picco.
 *
 * È il minimo di `@keyframes capture-target-pulse` in `GameBoard.css`, ricopiato qui perché il
 * resolver non legge il CSS. Che i due valori non divergano lo verifica un test:
 * abbassare il fondo del pulsare senza toccare questa costante taglierebbe la garanzia
 * dell'anello in silenzio.
 */
export const RING_MIN_ALPHA = 0.7;

/** Il tetto della scala. Oltre, il verde diventa un bianco verdino e smette di essere il verde. */
const MAX_LIGHTNESS = 0.95;

/** Il pavimento della scala. Sotto, il verde diventa nero e smette di essere un colore. */
const MIN_LIGHTNESS = 0.05;

/** Il passo con cui si sale: sotto, non cambia la scelta; sopra, si schiarisce più del necessario. */
const LIGHTNESS_STEP = 0.01;

function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

/** Il colore che si VEDE davvero: la tinta stesa sul fondo alla sua alpha. */
function compositeOver(fg: string, alpha: number, bg: string): string {
  const f = parseHex(fg);
  const b = parseHex(bg);
  return (
    '#' +
    f
      .map((c, i) => Math.round(alpha * c + (1 - alpha) * b[i]!))
      .map((c) => c.toString(16).padStart(2, '0'))
      .join('')
  );
}

/**
 * Quanto un candidato regge, in multipli della propria soglia. 1 = esattamente al limite.
 *
 * Entrambi i segni sono misurati COMPOSITI, cioè come si vedono: il pallino alla sua opacità
 * dimezzata, l'anello al fondo del suo pulsare. Misurare la tinta piena, e poi stenderla velata,
 * è precisamente l'errore che rese invisibile il primo pallino di questo progetto.
 */
function score(candidate: string, square: string): number {
  return Math.min(
    perceptualDistance(compositeOver(candidate, DOT_ALPHA, square), square) / DOT_MIN_DISTANCE,
    perceptualDistance(compositeOver(candidate, RING_MIN_ALPHA, square), square) / RING_MIN_DISTANCE,
  );
}

// Su una scacchiera esistono al massimo due caselle (chiara e scura), ma il resolver verrebbe
// chiamato per ogni pallino a ogni render. La cache lo riduce a due calcoli per partita — stessa
// ragione, e stessa forma, di `emblemColorCache` in `piece-themes.ts`.
const dotColorCache = new Map<string, string>();

/**
 * Il verde dei segni di destinazione su una casella di questo colore.
 *
 * @param squareHex colore della casella, esadecimale `#rrggbb` (per i temi che passano un token
 *                  CSS c'è `boardColorHex` in `board-themes.ts`).
 */
export function resolveMoveHintColor(squareHex: string): string {
  const cached = dotColorCache.get(squareHex);
  if (cached !== undefined) return cached;
  const value = computeMoveHintColor(squareHex);
  dotColorCache.set(squareHex, value);
  return value;
}

function computeMoveHintColor(squareHex: string): string {
  // Un ingresso che non è un esadecimale — tipicamente un `var(...)` non passato da
  // `boardColorHex` — non deve arrivare all'aritmetica del colore: là dentro diventerebbe NaN,
  // ogni confronto sarebbe falso e il risultato sarebbe comunque il verde di casa, ma per
  // accidente invece che per scelta. Restituirlo qui rende la cosa intenzionale e leggibile,
  // e toglie di mezzo un modo silenzioso di sbagliare chiamata.
  if (!/^#[0-9a-f]{6}$/i.test(squareHex)) return HL_DOT_BASE;

  const base = hexToOklch(HL_DOT_BASE);

  if (score(HL_DOT_BASE, squareHex) >= 1) return HL_DOT_BASE;

  let best = HL_DOT_BASE;
  let bestScore = score(HL_DOT_BASE, squareHex);

  // SI ESAURISCE PRIMA TUTTA LA DISCESA, e solo dopo si prova a salire.
  //
  // Alternare i due versi passo per passo — che è la cosa che viene in mente per prima — non
  // basta: su una casella verde di mezza chiarezza scendere richiede un salto lungo (bisogna
  // scavalcare tutta la tinta del fondo) mentre salire ne richiede uno corto, quindi vincerebbe
  // quasi sempre il chiaro proprio sui fondi per cui la preferenza esiste. Misurato: sull'inglese
  // l'alternanza sceglieva L 0.68, cioè un pallino più chiaro del glow. Cercare prima tutta la
  // discesa costa qualche iterazione in più e dà il comportamento che si è chiesto.
  //
  // Dentro ciascun verso si prende il PRIMO che ce la fa, non il migliore: allontanarsi oltre il
  // necessario è un altro modo di alzare la voce.
  for (const direction of [-1, 1]) {
    for (let step = LIGHTNESS_STEP; step <= MAX_LIGHTNESS; step += LIGHTNESS_STEP) {
      const L = base.L + direction * step;
      if (L < MIN_LIGHTNESS || L > MAX_LIGHTNESS) continue;
      const candidate = oklchToHex(L, base.C, base.h);
      const s = score(candidate, squareHex);
      if (s >= 1) return candidate;
      if (s > bestScore) {
        bestScore = s;
        best = candidate;
      }
    }
  }

  // Ripiego: nessuna chiarezza regge entrambe le soglie. Sui fondi che spediamo non succede mai
  // (lo verifica il test); ci si arriva solo con un tema personalizzato scelto apposta sulla
  // stessa tinta. Meglio il meno peggio che un colore fisso che lì sarebbe invisibile.
  return best;
}
